import { ReactNode } from 'react';
import { Plural, Trans } from '@lingui/react/macro';

import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import IconPackage from '@/icons/Package.svg';
import IconShipmentDone from '@/icons/ShipmentDone.svg';
import IconTruck from '@/icons/Truck.svg';
import { BrandRequestMainContent } from '@/layouts/Brand/BrandRequestLayout/BrandRequestLayout';
import { isCancelled, RequestWithRelations } from '@/models/request';
import { ClientArticlesList } from '@/routes/Brand/Requests/Request/components/shared/ClientArticlesList';
import ClientStepper from '@/routes/Brand/Requests/Request/components/shared/ClientStepper';
import useViewPort from '@/utils/useViewport';

export const Delivery = ({ request }: { request: RequestWithRelations }) => {
  let messageContent: ReactNode | null = null;
  const { isMobile } = useViewPort();

  const articlesInTransitStep = request.articles.filter(
    (article) => article.step?.step === 'transit'
  );

  const areAllArticlesPendingClientPickup = articlesInTransitStep.every(
    (article) =>
      article.step?.step === 'transit' &&
      !!request.storeId &&
      article.atEntityId === request.storeId &&
      article.toClient &&
      article.step.config.clientPickup
  );

  if (areAllArticlesPendingClientPickup && isCancelled(request)) {
    const paymentRefused = request.articles.find((article) => !!article.paymentRefusedAt);
    const validationRefused = request.articles.find((article) => !!article.quoteRefusedAt);

    return (
      <BrandRequestMainContent>
        <Stack gap="1.5rem">
          <Stack row gap="0.5rem">
            <Title size={isMobile ? '300' : '200'} weight="bold">
              <Trans id="store.request.delivery.quote-refused.title">
                The request has been archived
              </Trans>
            </Title>
            <Paragraph size={isMobile ? '100' : '50'}>
              {paymentRefused || validationRefused ? (
                <Trans id="store.request.delivery.initial-quote-refused">
                  The client refused the quote.
                </Trans>
              ) : null}{' '}
              <Trans id="store.request.delivery.store-pickup">
                They have been notified to come pick-up their{' '}
                <Plural value={articlesInTransitStep.length} one="item" other="items" /> in your
                store.
              </Trans>
            </Paragraph>
          </Stack>
          <ClientArticlesList request={request} showArticleComment />
        </Stack>
      </BrandRequestMainContent>
    );
  }

  const articlesNotInPendingClientPickup = articlesInTransitStep.filter(
    (article) =>
      article.step?.step === 'transit' &&
      !(
        !!request.storeId &&
        article.atEntityId === request.storeId &&
        article.toClient &&
        article.step.config.clientPickup
      )
  );

  const areAllArticlesInTransit = articlesNotInPendingClientPickup.every(
    (article) => article.step?.step === 'transit' && article.inTransit
  );

  if (areAllArticlesPendingClientPickup) {
    messageContent = (
      <>
        <Stack row gap="0.5rem" flexWrap="nowrap">
          <IconShipmentDone className="color-primary-700" style={{ fontSize: '24px' }} />
          <Paragraph size={isMobile ? '100' : '50'}>
            <Trans id="store.request.delivery.pending-client">
              The <Plural value={articlesInTransitStep.length} one="item has" other="items have" />{' '}
              been successfully delivered to your store. The client has been notified to come pick{' '}
              <Plural value={articlesInTransitStep.length} one="it" other="them" /> up.
            </Trans>
          </Paragraph>
        </Stack>
      </>
    );
  } else if (!areAllArticlesInTransit) {
    messageContent = (
      <Stack row gap="0.5rem" flexWrap="nowrap">
        <IconPackage className="color-tertiary-700" style={{ fontSize: '24px' }} />
        <Paragraph size={isMobile ? '100' : '50'}>
          {(!request.client || request.client.deliveryOption === 'store') && (
            <Trans id="store.request.delivery.pending.store">
              The workshop is preparing the{' '}
              <Plural
                value={articlesNotInPendingClientPickup.length}
                one="item for its"
                other="items for their"
              />{' '}
              shipment back to your store.
            </Trans>
          )}
          {request.client?.deliveryOption !== 'store' && (
            <Trans id="store.request.delivery.pending.client">
              The workshop is preparing the{' '}
              <Plural
                value={articlesNotInPendingClientPickup.length}
                one="item for its"
                other="items for their"
              />{' '}
              shipment back to the client.
            </Trans>
          )}
        </Paragraph>
      </Stack>
    );
  } else {
    messageContent = (
      <Stack row gap="0.5rem" flexWrap="nowrap">
        <IconTruck className="color-tertiary-700" style={{ fontSize: '24px' }} />
        <Paragraph size={isMobile ? '100' : '50'}>
          {(!request.client || request.client.deliveryOption === 'store') && (
            <Trans id="store.request.delivery.in-transit.store">
              The{' '}
              <Plural
                value={articlesNotInPendingClientPickup.length}
                one="item is on its"
                other="items are on their"
              />{' '}
              way back to your store.
            </Trans>
          )}
          {request.client?.deliveryOption !== 'store' && (
            <Trans id="store.request.delivery.in-transit.client">
              The{' '}
              <Plural
                value={articlesNotInPendingClientPickup.length}
                one="item is on its"
                other="items are on their"
              />{' '}
              way back to the client.
            </Trans>
          )}
        </Paragraph>
      </Stack>
    );
  }

  return (
    <BrandRequestMainContent>
      <Stack gap="1.5rem">
        <ClientStepper request={request} />
        {messageContent}
        <ClientArticlesList request={request} showPrice={!!request.client} showArticleComment />
      </Stack>
    </BrandRequestMainContent>
  );
};
